import * as XLSX from 'xlsx';
import { GRADE_LEVELS } from '../../../shared/constant/gradeLevels';
import { STRANDS_BY_GRADE } from '../../../shared/constant/strands';
import { formatPhMobileInput, isValidPhMobile } from '../../../shared/utils/phoneNumber';
import { isDepedSf1Sheet, extractDepedSf1 } from './parseDepedSf1';

const LRN_PATTERN = /^[0-9]{12}$/;

function validateRow(raw) {
  const errors = [];

  const lastname = String(raw.lastname ?? '').trim();
  const firstname = String(raw.firstname ?? '').trim();
  const middlename = String(raw.middlename ?? '').trim();
  const grade_level = String(raw.grade_level ?? '').trim();
  const section = String(raw.section ?? '').trim();
  const strand = String(raw.strand ?? '').trim().toUpperCase();
  const guardian_name = String(raw.guardian_name ?? '').trim();
  const guardian_contact = formatPhMobileInput(String(raw.guardian_contact ?? '').trim());
  const province = String(raw.province ?? '').trim();
  const town = String(raw.town ?? '').trim();
  const brgy = String(raw.brgy ?? '').trim();
  const school_year = String(raw.school_year ?? '').trim();
  const lrn = String(raw.lrn ?? '').trim();

  if (!lastname) errors.push('Last name is required');
  if (!firstname) errors.push('First name is required');

  if (!grade_level) {
    errors.push('Grade level is required');
  } else if (!GRADE_LEVELS.includes(grade_level)) {
    errors.push(`Invalid grade level "${grade_level}"`);
  }

  if (lrn && !LRN_PATTERN.test(lrn)) {
    errors.push('LRN must be exactly 12 digits');
  }

  if (guardian_contact && !isValidPhMobile(guardian_contact)) {
    errors.push('Guardian contact must be in the format 09XX-XXX-XXXX');
  }

  const strandOptions = STRANDS_BY_GRADE[grade_level] ?? [];
  let finalStrand = '';
  if (strandOptions.length > 0) {
    if (!strand) {
      errors.push(`Strand is required for ${grade_level}`);
    } else if (!strandOptions.some((s) => s.code === strand)) {
      errors.push(`Invalid strand "${strand}" for ${grade_level}`);
    } else {
      finalStrand = strand;
    }
  }

  return {
    errors,
    data: {
      lrn: lrn || null,
      lastname,
      firstname,
      middlename: middlename || null,
      grade_level,
      section: section || null,
      strand: finalStrand || null,
      guardian_name: guardian_name || null,
      guardian_contact: guardian_contact || null,
      province: province || null,
      town: town || null,
      brgy: brgy || null,
      school_year: school_year || null,
      status: 'Active',
    },
  };
}

export function buildImportRows(rawRows, existingLrns = []) {
  const rows = rawRows.map((raw, index) => {
    const { errors, data } = validateRow(raw);
    return { rowNumber: index + 2, data, errors, valid: errors.length === 0 };
  });

  const existingLrnSet = new Set(existingLrns.filter(Boolean));
  const rowNumbersByLrn = new Map();
  for (const row of rows) {
    if (!row.data.lrn) continue;
    if (!rowNumbersByLrn.has(row.data.lrn)) rowNumbersByLrn.set(row.data.lrn, []);
    rowNumbersByLrn.get(row.data.lrn).push(row.rowNumber);
  }

  for (const row of rows) {
    const lrn = row.data.lrn;
    if (!lrn) continue;

    if (existingLrnSet.has(lrn)) {
      row.errors.push(`Duplicate LRN (already exists in system)`);
    } else if (rowNumbersByLrn.get(lrn).length > 1) {
      const otherRows = rowNumbersByLrn.get(lrn).filter((n) => n !== row.rowNumber);
      row.errors.push(`Duplicate LRN (also in row ${otherRows.join(', ')})`);
    }
    row.valid = row.errors.length === 0;
  }

  return rows;
}

// Reads an uploaded workbook and auto-detects whether it's a plain flat template
// or a DepEd School Form 1 (SF1/SF1-SHS) export. For SF1 files, returns `meta`
// (grade level, section, school year, detected strand) and `rawRows` so the caller
// can let the user confirm/override the strand and re-validate without re-reading the file.
export function parseUploadedWorkbook(arrayBuffer, existingLrns = []) {
  const workbook = XLSX.read(arrayBuffer, { type: 'array' });
  const sheetName =
    workbook.SheetNames.find((n) => /school_form_1/i.test(n)) ||
    (workbook.SheetNames.includes('Students') ? 'Students' : workbook.SheetNames[0]);
  const sheet = workbook.Sheets[sheetName];
  const aoaRows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: false });

  if (isDepedSf1Sheet(aoaRows)) {
    const { meta, rawRows } = extractDepedSf1(aoaRows);
    return { format: 'sf1', meta, rawRows, rows: buildImportRows(rawRows, existingLrns) };
  }

  const rawRows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
  return { format: 'flat', meta: null, rawRows, rows: buildImportRows(rawRows, existingLrns) };
}

export function applyStrandOverride(rawRows, strand, existingLrns = []) {
  const updated = rawRows.map((row) => ({ ...row, strand }));
  return { rawRows: updated, rows: buildImportRows(updated, existingLrns) };
}
